/**
 * 瓦斯行邀請碼（shopCode）與瓦斯行 id（supplier）的「跨流程攜帶」工具。
 *
 * 與 familyInvite 的差異：shopCode 是「每家瓦斯行一組、共用」，
 * 用於驗證 onboarding 入口宣稱的瓦斯行與後端 CIS 推導出的瓦斯行是否一致。
 *
 * 使用情境：入口網址帶 `?supplier=&shopCode=`（傳單連結、瓦斯行 OA 的
 * rich menu），需在 LINE login 的 redirect round-trip 期間存活，
 * 註冊送出時一併帶到 /line/register。
 *
 * 兩個值同進同出：supplier 決定 Onboarding 的 isFlyerKnown（是否對老客戶
 * 做資料比對），少了它即使 shopCode 還在，老客戶也會被當成新客戶。
 */
const KEY = "shop_invite_code";
const SUPPLIER_KEY = "shop_entry_supplier";

/** 淨化成英數字元集、上限 12 碼；不合法回空字串。 */
const sanitize = (raw: string | null | undefined): string =>
  (raw ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12); // 長度/字元集：見設計 spec §7 open question

/** supplier_id 大小寫敏感，只 trim 不做字元集轉換。 */
const sanitizeSupplier = (raw: string | null | undefined): string =>
  (raw ?? "").trim().slice(0, 64);

/**
 * 進站時呼叫：若目前 URL 帶 `?shopCode=`，連同 `?supplier=` 一起存進
 * sessionStorage 以跨流程攜帶。沒有 shopCode 時不動既有值。
 */
export const captureShopInviteFromUrl = (): void => {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = sanitize(params.get("shopCode"));
    if (!code) return;
    sessionStorage.setItem(KEY, code);
    const supplier = sanitizeSupplier(params.get("supplier"));
    if (supplier) {
      sessionStorage.setItem(SUPPLIER_KEY, supplier);
    } else {
      sessionStorage.removeItem(SUPPLIER_KEY);
    }
  } catch {
    // 忽略：取不到就當作沒有 shopCode
  }
};

/** 取得已攜帶的瓦斯行邀請碼；沒有則 null。 */
export const getShopInviteCode = (): string | null => {
  if (typeof window === "undefined") return null;
  const code = sanitize(sessionStorage.getItem(KEY));
  return code || null;
};

/** 取得已攜帶的瓦斯行 id；沒有則 null。 */
export const getShopEntrySupplierId = (): string | null => {
  if (typeof window === "undefined") return null;
  const supplier = sanitizeSupplier(sessionStorage.getItem(SUPPLIER_KEY));
  return supplier || null;
};

/** 用掉/離開後清除，避免殘留影響後續流程。 */
export const clearShopInviteCode = (): void => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(KEY);
  sessionStorage.removeItem(SUPPLIER_KEY);
};
